import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  styled,
  ButtonBase,
  CircularProgress,
} from "@mui/material";
import {
  getFacilityTypes,
  transformFacilityTypesToCategories,
  transformFacilityTypesToSections,
  createCategoryToSectionsMap,
  TransformedCategory,
  TransformedSection,
  FacilityType,
} from "@services/facilitytype";
import RegulationsTab from "./RegulationsTab";
import { Regulation } from "@services/regulations";
import { getSelectedCountryCode } from "@utils/countryUtils";
import { getSelectedFacilityFromSession } from "@utils/facilityUtils";
import { canViewByName, FEATURE_NAMES } from "@utils/permissions";

const ContentContainer = styled(Box)({
  backgroundColor: "#fdffff",
  borderRadius: "12px",
  height: "100%",
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  position: "relative",
  width: "100%",
  boxSizing: "border-box",
});

/** Discovery title band is 80px with the title at the bottom (`alignItems: flex-end`). */
const DISCOVERY_TITLE_BAND_HEIGHT_PX = 80;
/** Extra space above the title baseline (80 + 20 = 100px band). */
const REGULATIONS_TITLE_EXTRA_TOP_PX = 20;
const REGULATIONS_TITLE_BAND_HEIGHT_PX =
  DISCOVERY_TITLE_BAND_HEIGHT_PX + REGULATIONS_TITLE_EXTRA_TOP_PX;

/** Header shell — title band matches Discovery; Facilities/Regulations tabs sit below. */
const PageHeader = styled(Box)({
  backgroundColor: "rgba(253, 255, 255, 0.2)",
  borderBottom: "1px solid #35d7e4",
  flexShrink: 0,
  boxSizing: "border-box",
});

const TitleHeaderRow = styled(Box)(({ theme }) => ({
  padding: "0px 64px 24px",
  display: "flex",
  alignItems: "flex-end",
  height: `${REGULATIONS_TITLE_BAND_HEIGHT_PX}px`,
  minHeight: `${REGULATIONS_TITLE_BAND_HEIGHT_PX}px`,
  flexShrink: 0,
  boxSizing: "border-box",
  [theme.breakpoints.down("md")]: {
    padding: "0px 16px 16px",
    height: "auto",
    minHeight: `${REGULATIONS_TITLE_BAND_HEIGHT_PX}px`,
    alignItems: "flex-end",
  },
}));

const TabsHeaderRow = styled(Box)(({ theme }) => ({
  padding: "0px 64px 0px",
  boxSizing: "border-box",
  [theme.breakpoints.down("md")]: {
    padding: "0px 16px 0px",
  },
}));

const ContentArea = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: "16px 48px",
  overflowY: "auto",
  overflowX: "hidden",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  boxSizing: "border-box",
  minHeight: 0,
  width: "100%",
  [theme.breakpoints.down("md")]: {
    padding: "12px 16px",
  },
}));

const TabContentContainer = styled(Box)({
  width: "100%",
  boxSizing: "border-box",
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
});

/** Matches Discovery `HeaderLeft`. */
const HeaderTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const BookIcon = styled(Box)({
  width: "28px",
  height: "28px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const Title = styled(Typography)({
  fontSize: "28px",
  fontWeight: 500,
  color: "#262626",
  letterSpacing: "-1px",
  lineHeight: 1.5,
  fontFamily: "Rethink Sans, sans-serif",
  margin: 0,
});

const CategoriesSection = styled(Box)({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  flex: 1,
  minHeight: 0,
  boxSizing: "border-box",
  overflow: "hidden",
});

const CategoriesGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(165px, 1fr))",
  gap: "15px",
  marginBottom: "16px",
  width: "100%",
  boxSizing: "border-box",
});

const CategoryCard = styled(ButtonBase)<{ isActive?: boolean }>(
  ({ isActive }) => ({
    position: "relative",
    // flex: "1 1 0", // responsive
    // width: "175px",
    height: "140px",
    minWidth: "175px", // adjust as needed
    maxWidth: "100%", // don't overflow

    padding: "35px 15px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    textAlign: "left",
    border: "none",
    cursor: "pointer",
    borderRadius: "12px",
    overflow: "hidden",
    gap: 10,
    // Folder shape (tab on top left)
    clipPath:
      "path('M0,20 Q0,0 20,0 H90 Q100,0 110,10 H220 Q220,10 220,30 V180 H0 Z')",

    background: isActive
      ? "linear-gradient(101deg, #F9FEFF  8.13%, #F3FAFC 93.84%)"
      : "linear-gradient(to right, #D7F8FF, #D7F8FF, #D7F8FF, #D7F8FF)",

    // border: "1px solid #D1F0F7",
    boxShadow: isActive
      ? "-4px -2px 4px 0 #EBFCFF inset, 0 5px 4px 0 #EBFCFF inset, 0 0 6px rgba(0, 166, 180, 0.2)"
      : "none",

    transition: "all 0.2s ease",
  })
);

const CategoryTitle = styled(Typography)({
  // fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "16px",
  color: "#065b70",
  textAlign: "left",
  lineHeight: "normal",
});

const CategoryFooter = styled(Box)<{ isActive?: boolean }>(({ isActive }) => ({
  width: "100%",
  height: "20px",
  borderRadius: "0 0 12px 12px",
  backgroundColor: "#d1f0f7",
  border: "1px solid #d1f0f7",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "absolute",
  bottom: 0,
  left: 0,

  borderRight: "1px solid #D1F0F7",
  borderBottom: "1px solid #D1F0F7",
  borderLeft: "1px solid #D1F0F7",
  background: isActive
    ? "linear-gradient(180deg, #C0F4FF 0%, #B0EAF5 100%)"
    : "linear-gradient(180deg, #3095AB 0%, #2D6673 100%);",
}));

const ChevronContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 0,
  lineHeight: 0, // prevents extra space from inline elements
  // borderRadius: "0 0 12px 12px",
  // borderRight: "1px solid #D1F0F7",
  // borderBottom: "1px solid #D1F0F7",
  // borderLeft: "1px solid #D1F0F7",
  // background: "linear-gradient(180deg, #C0F4FF 0%, #B0EAF5 100%)",
  // color: "#fff",
  backgroundColor: "transparent",
});

const SectionsContainer = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", // responsive grid
  gap: "16px",
  width: "100%",
  padding: "8px",
  overflowY: "scroll",
  maxHeight: "300px",
  paddingBottom: "10px",
});

const SectionCard = styled(Box)({
  backgroundColor: "#ffffff",
  border: "1px solid #D6E9ED",
  borderRadius: "8px",
  boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.08)",
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "space-between",
  cursor: "pointer",
  minHeight: "150px",
  transition: "all 0.2s ease-in-out",
  borderColor: "#0A829D",
  "&:hover": {
    backgroundColor: "#F3FDFF",

    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.12)",
  },
  position: "relative",
});

const SectionContent = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

const SectionTitleContainer = styled(Box)({
  padding: "4px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const SectionTitle = styled(Typography)({
  //fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "16px",
  color: "#065b70",
  lineHeight: "normal",
  flex: 1,
  display: "flex", // 👈 make it flex
  alignItems: "flex-start", // 👈 vertically center icon + text
  gap: "8px",
});
const IconWrapper = styled("span")({
  display: "inline-flex",
  width: "20px", // 👈 fixed size so it won’t shrink
  height: "20px",
  flexShrink: 0, // 👈 prevents shrinking when text wraps
});
const SectionDescription = styled(Box)({
  padding: "4px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const SectionDescText = styled(Typography)({
  // fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 400,
  fontSize: "14px",
  color: "#4f4f4f",
  lineHeight: "normal",
  flex: 1,
});

const ExpandButton = styled(Box)({
  position: "absolute", // 👈 pin it to card corner
  bottom: 0,
  right: 0,
  width: "32px",
  height: "32px",
  backgroundColor: "#0A829D",
  border: "none",
  borderTopLeftRadius: "6px", // 👈 only top-left rounded, rest square
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "background-color 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: "#076173",
  },
  "& svg": {
    stroke: "#fff",
  },
});

interface SpacesContentProps {
  selectedCategory?: string;
  onCategorySelect?: (selectedCategory: string) => void;
  onSectionClick?: (section: TransformedSection) => void;
  onRegulationClick?: (regulation: Regulation) => void;
}

// Loading component
const LoadingContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  height: "80vh",
  width: "100%",
  gap: "8px",
});

// Error component
const ErrorContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  height: "200px",
  width: "100%",
  padding: "20px",
  textAlign: "center",
});

const ErrorText = styled(Typography)({
  color: "#d32f2f",
  marginBottom: "16px",
});

const RetryButton = styled(Button)({
  backgroundColor: "#0a829d",
  color: "white",
  "&:hover": {
    backgroundColor: "#065b70",
  },
});

export default function SpacesContent({
  selectedCategory = "hospitals--inpatient",
  onCategorySelect,
  onSectionClick,
  onRegulationClick,
}: SpacesContentProps) {
  // State management
  const [, setExpandedSections] = useState<
    Record<string, boolean>
  >({});
  const [categories, setCategories] = useState<TransformedCategory[]>([]);
  const [sections, setSections] = useState<TransformedSection[]>([]);
  const [categoryToSectionsMap, setCategoryToSectionsMap] = useState<
    Record<string, string[]>
  >({});
  const [, setAllFacilityTypes] = useState<FacilityType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentCategory, setCurrentCategory] = useState(selectedCategory);
  const [activeTab, setActiveTab] = useState<"facilities" | "regulations">(
    "facilities"
  );
  const selectedSessionFacility = getSelectedFacilityFromSession<{
    facility_type_slug?: string;
    country_code?: string;
  }>();
  // Load facility types from API
  const loadFacilityTypes = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getFacilityTypes();
      const facilityTypes = response.facility_types;

      // Transform API data
      const transformedCategories =
        transformFacilityTypesToCategories(facilityTypes);
      const transformedSections =
        transformFacilityTypesToSections(facilityTypes);
      const categoryMapping = createCategoryToSectionsMap(
        transformedCategories
      );

      // Set initial selected category if none provided
      const initialCategory =
        selectedCategory ||
        (transformedCategories.length > 0 ? transformedCategories[0].id : "");

      setCategories(transformedCategories);
      setSections(transformedSections);
      setCategoryToSectionsMap(categoryMapping);
      setAllFacilityTypes(facilityTypes);
      setCurrentCategory(initialCategory);

      // Auto-expand first section of selected category
      const relevantSectionIds = categoryMapping[initialCategory] || [];
      if (relevantSectionIds.length > 0) {
        setExpandedSections({ [relevantSectionIds[0]]: true });
      }
    } catch (err) {
      console.error("Failed to load facility types:", err);
      setError(
        err instanceof Error ? err.message : "Failed to load facility types"
      );
    } finally {
      setLoading(false);
    }
  };
  const TabsContainer = styled(Box)({
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    width: "100%",
  });

  const TabButton = styled(ButtonBase)<{ isActive?: boolean }>(
    ({ isActive }) => ({
      padding: "8px 12px 4px 12px",
      borderBottom: isActive ? "1px solid #065b70" : "1px solid transparent",
      backgroundColor: "transparent",
      cursor: "pointer",
      position: "sticky",
      top: 0,
      zIndex: 1,
      marginBottom: isActive ? "-1px" : "0px",
    })
  );

  const TabText = styled(Typography)<{ isActive?: boolean }>(
    ({ isActive }) => ({
      fontWeight: 500,
      fontSize: "15px",
      color: isActive ? "#065b70" : "#737373",
      lineHeight: 1.5,
      whiteSpace: "nowrap",
    })
  );

  // Load data on component mount
  useEffect(() => {
    loadFacilityTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh when selected country changes (listen to localStorage changes)
  useEffect(() => {
    let current = getSelectedCountryCode();
    const check = () => {
      const latest = getSelectedCountryCode();
      if (latest !== current) {
        current = latest;
        // eslint-disable-next-line no-console
        console.log("[SpacesContent] Country changed →", latest, "reloading facility types...");
        loadFacilityTypes();
      }
    };
    window.addEventListener("storage", check);
    const id = window.setInterval(check, 500);
    return () => {
      window.removeEventListener("storage", check);
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Respect target tab set by other pages via sessionStorage
  useEffect(() => {
    try {
      const target = sessionStorage.getItem("spaces_target_tab");
      if (target === "facilities" || target === "regulations") {
        setActiveTab(target as typeof activeTab);
        sessionStorage.removeItem("spaces_target_tab");
      }
    } catch (_) {
      // ignore storage errors
    }
  }, []);

  // Update sections when category changes
  useEffect(() => {
    if (currentCategory && categoryToSectionsMap[currentCategory]) {
      const relevantSectionIds = categoryToSectionsMap[currentCategory] || [];
      if (relevantSectionIds.length > 0) {
        setExpandedSections({ [relevantSectionIds[0]]: true });
      } else {
        setExpandedSections({});
      }
    }
  }, [currentCategory, categoryToSectionsMap]);

  // Update current category when selectedCategory prop changes
  useEffect(() => {
    if (selectedCategory && selectedCategory !== currentCategory) {
      setCurrentCategory(selectedCategory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const handleTabClick = (tab: "facilities" | "regulations") => {
    setActiveTab(tab);
  };
  const handleCategoryClick = (categoryId: string) => {
    console.log("handleCategoryClick", categoryId);
    setCurrentCategory(categoryId);
    if (onCategorySelect) {
      onCategorySelect(categoryId);
    }
  };

  const handleRetry = () => {
    loadFacilityTypes();
  };

  const handleSectionClick = (sectionId: string) => {
    const section = filteredSections.find((s) => s.id === sectionId);
    const selectedCategoryDetails = categories.find(
      (cat) => cat.id === selectedCategory
    );
    if (selectedCategoryDetails) {
      sessionStorage.setItem("CategoryName", selectedCategoryDetails.title);
    }
    if (section && onSectionClick) {
      onSectionClick(section);
      return;
    }
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Filter sections based on selected category using the mapping
  const filteredSections = sections.filter((section) => {
    const relevantSectionIds = categoryToSectionsMap[currentCategory] || [];
    return relevantSectionIds.includes(section.id);
  });

  console.log("selectedCategory", currentCategory);

  // Show loading state
  if (loading) {
    return (
      <ContentContainer>
        <LoadingContainer>
          <CircularProgress size={40} sx={{ color: "#0a829d" }} />
          <Typography sx={{ ml: 2, color: "#737373" }}>
            Loading facility types...
          </Typography>
        </LoadingContainer>
      </ContentContainer>
    );
  }

  // Show error state
  if (error) {
    return (
      <ContentContainer>
        <ErrorContainer>
          <ErrorText variant="h6">Failed to load facility types</ErrorText>
          <Typography variant="body2" sx={{ color: "#737373", mb: 2 }}>
            Please check your connection and try again
          </Typography>
          <RetryButton onClick={handleRetry} variant="contained">
            Retry
          </RetryButton>
        </ErrorContainer>
      </ContentContainer>
    );
  }

  const canViewSetupFacility = canViewByName(FEATURE_NAMES.SETUP_FACILITY);
  return (
    <ContentContainer>
      {canViewSetupFacility && (
        <>
          <PageHeader>
            <TitleHeaderRow
              data-testid="regulations-library-title-row"
              style={{
                height: REGULATIONS_TITLE_BAND_HEIGHT_PX,
                minHeight: REGULATIONS_TITLE_BAND_HEIGHT_PX,
                padding: "0 64px 24px",
                display: "flex",
                alignItems: "flex-end",
                boxSizing: "border-box",
              }}
            >
              <HeaderTitle>
                <BookIcon>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="28"
                    height="28"
                    viewBox="0 0 20 21"
                    fill="none"
                  >
                    <path
                      d="M2 4C2 2.61929 3.11929 1.5 4.5 1.5H13.5C14.8807 1.5 16 2.61929 16 4V6.5H15V4C15 3.17157 14.3284 2.5 13.5 2.5H4.5C3.67157 2.5 3 3.17157 3 4V13C3 13.8284 3.67157 14.5 4.5 14.5H8V15.5H4.5C3.11929 15.5 2 14.3807 2 13V4ZM5.5 6.5H11C10.1115 6.5 9.31321 6.88625 8.76389 7.5H5.5C5.22386 7.5 5 7.27614 5 7C5 6.72386 5.22386 6.5 5.5 6.5ZM8.17071 8.5H5.5C5.22386 8.5 5 8.72386 5 9C5 9.27614 5.22386 9.5 5.5 9.5L8 9.5C8 9.14936 8.06015 8.81278 8.17071 8.5ZM5.5 4.5C5.22386 4.5 5 4.72386 5 5C5 5.27614 5.22386 5.5 5.5 5.5H12.5C12.7761 5.5 13 5.27614 13 5C13 4.72386 12.7761 4.5 12.5 4.5H5.5ZM18 9.5C18 8.39543 17.1046 7.5 16 7.5H11C9.89543 7.5 9 8.39543 9 9.5V17.5C9 18.6046 9.89543 19.5 11 19.5H17.5C17.7761 19.5 18 19.2761 18 19C18 18.7239 17.7761 18.5 17.5 18.5H11C10.4477 18.5 10 18.0523 10 17.5V17.4969H17.5C17.7761 17.4969 18 17.2731 18 16.9969V9.5ZM10 16.4969V9.5C10 8.94771 10.4477 8.5 11 8.5H16C16.5523 8.5 17 8.94772 17 9.5V16.4969H10Z"
                      fill="#262626"
                    />
                  </svg>
                </BookIcon>
                <Title>Regulations Library</Title>
              </HeaderTitle>
            </TitleHeaderRow>
            <TabsHeaderRow>
              <TabsContainer>
                <TabButton
                  isActive={activeTab === "facilities"}
                  onClick={() => handleTabClick("facilities")}
                >
                  <TabText isActive={activeTab === "facilities"}>
                    Facilities
                  </TabText>
                </TabButton>
                <TabButton
                  isActive={activeTab === "regulations"}
                  onClick={() => handleTabClick("regulations")}
                >
                  <TabText isActive={activeTab === "regulations"}>
                    Regulations
                  </TabText>
                </TabButton>
              </TabsContainer>
            </TabsHeaderRow>
          </PageHeader>
          <ContentArea>
            <TabContentContainer>
              {activeTab === "facilities" ? (
                <CategoriesSection>
                  <CategoriesGrid>
                    {categories.map((category) => {
                      // ✅ define it here
                      const isActive = currentCategory === category.id;

                      return (
                        <CategoryCard
                          key={category.id}
                          isActive={isActive}
                          onClick={() => handleCategoryClick(category.id)}
                        >
                          <CategoryTitle>{category.title}</CategoryTitle>
                          <CategoryFooter isActive={isActive}>
                            <ChevronContainer>
                              {isActive ? (
                                // Up Chevron (active)

                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="12"
                                  height="15"
                                  viewBox="0 0 12 15"
                                  fill="none"
                                >
                                  <path
                                    d="M2.14645 10.3536C2.34171 10.5488 2.65829 10.5488 2.85355 10.3536L6 7.20711L9.14645 10.3536C9.34171 10.5488 9.65829 10.5488 9.85355 10.3536C10.0488 10.1583 10.0488 9.84171 9.85355 9.64645L6.35355 6.14645C6.15829 5.95118 5.84171 5.95118 5.64645 6.14645L2.14645 9.64645C1.95118 9.84171 1.95118 10.1583 2.14645 10.3536Z"
                                    fill="#0A829D"
                                  />
                                  <path
                                    d="M2.14645 7.35355C2.34171 7.54882 2.65829 7.54882 2.85355 7.35355L6 4.20711L9.14645 7.35355C9.34171 7.54882 9.65829 7.54882 9.85355 7.35355C10.0488 7.15829 10.0488 6.84171 9.85355 6.64645L6.35355 3.14645C6.15829 2.95118 5.84171 2.95118 5.64645 3.14645L2.14645 6.64645C1.95118 6.84171 1.95118 7.15829 2.14645 7.35355Z"
                                    fill="#0A829D"
                                  />
                                </svg>
                              ) : (
                                // Down Chevron (inactive)
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="12"
                                  height="15"
                                  viewBox="0 0 12 15"
                                  fill="none"
                                >
                                  <path
                                    d="M2.14645 4.64645C2.34171 4.45118 2.65829 4.45118 2.85355 4.64645L6 7.79289L9.14645 4.64645C9.34171 4.45118 9.65829 4.45118 9.85355 4.64645C10.0488 4.84171 10.0488 5.15829 9.85355 5.35355L6.35355 8.85355C6.15829 9.04882 5.84171 9.04882 5.64645 8.85355L2.14645 5.35355C1.95118 5.15829 1.95118 4.84171 2.14645 4.64645Z"
                                    fill="#FFFFFF"
                                  />
                                  <path
                                    d="M2.14645 7.64645C2.34171 7.45118 2.65829 7.45118 2.85355 7.64645L6 10.7929L9.14645 7.64645C9.34171 7.45118 9.65829 7.45118 9.85355 7.64645C10.0488 7.84171 10.0488 8.15829 9.85355 8.35355L6.35355 11.8536C6.15829 12.0488 5.84171 12.0488 5.64645 11.8536L2.14645 8.35355C1.95118 8.15829 1.95118 7.84171 2.14645 7.64645Z"
                                    fill="#FFFFFF"
                                  />
                                </svg>
                              )}
                            </ChevronContainer>
                          </CategoryFooter>
                        </CategoryCard>
                      );
                    })}
                  </CategoriesGrid>

                  <SectionsContainer>
                    {filteredSections.map((section) => (
                      <SectionCard
                        key={section.id}
                        onClick={() => handleSectionClick(section.id)}
                      >
                        <SectionContent>
                          <SectionTitleContainer>
                            <SectionTitle>
                              <IconWrapper>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  viewBox="0 0 20 20"
                                  fill="none"
                                >
                                  <path
                                    d="M3 6C3 4.34315 4.34315 3 6 3H14C15.6569 3 17 4.34315 17 6V14C17 15.6569 15.6569 17 14 17H6C4.34315 17 3 15.6569 3 14V6ZM12.6914 7.03794C12.6324 7.01349 12.5678 7 12.5 7H7.5C7.22386 7 7 7.22386 7 7.5C7 7.77614 7.22386 8 7.5 8H11.2929L7.14645 12.1464C6.95118 12.3417 6.95118 12.6583 7.14645 12.8536C7.34171 13.0488 7.65829 13.0488 7.85355 12.8536L12 8.70711V12.5C12 12.7761 12.2239 13 12.5 13C12.7761 13 13 12.7761 13 12.5V7.5C13 7.43221 12.9865 7.36756 12.9621 7.30861C12.938 7.25051 12.9026 7.19602 12.8557 7.14857L12.8514 7.14433C12.804 7.09744 12.7495 7.06198 12.6914 7.03794Z"
                                    fill="#065B70"
                                  />
                                </svg>
                              </IconWrapper>
                              {section.title}
                            </SectionTitle>
                          </SectionTitleContainer>
                          <SectionDescription>
                            <SectionDescText>
                              {section.description}
                            </SectionDescText>
                          </SectionDescription>
                        </SectionContent>
                        <ExpandButton>
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M7 17L17 7M17 7H9M17 7V15"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </ExpandButton>
                      </SectionCard>
                    ))}
                  </SectionsContainer>
                </CategoriesSection>
              ) : (
                <RegulationsTab
                  onRegulationClick={onRegulationClick}
                  countryCode={
                    selectedSessionFacility?.country_code || getSelectedCountryCode()
                  }
                  facilityTypeSlug={selectedSessionFacility?.facility_type_slug}
                />
              )}
            </TabContentContainer>
          </ContentArea>
        </>
      )}

        {/* <FadeOverlay /> */}

        {/* <ActionButtons>
          <ActionButton>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
            >
              <path
                d="M10 10.5C10 9.11929 11.1193 8 12.5 8H19.5C20.8807 8 22 9.11929 22 10.5V23.5C22 23.6881 21.8945 23.8602 21.7269 23.9456C21.5593 24.0309 21.358 24.015 21.2059 23.9044L16 20.1182L10.7941 23.9044C10.642 24.015 10.4407 24.0309 10.2731 23.9456C10.1055 23.8602 10 23.6881 10 23.5V10.5ZM12.5 9C11.6716 9 11 9.67157 11 10.5V22.5181L15.7059 19.0956C15.8812 18.9681 16.1188 18.9681 16.2941 19.0956L21 22.5181V10.5C21 9.67157 20.3284 9 19.5 9H12.5Z"
                fill="#065B70"
              />
            </svg>
          </ActionButton>
          <ActionButton>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="14"
              viewBox="0 0 15 14"
              fill="none"
            >
              <path
                d="M10.3298 9.83772L14.8269 5.41512L14.8841 5.34968C15.0558 5.12058 15.0367 4.78916 14.8268 4.58283L10.3297 0.161651L10.268 0.108643C9.9078 -0.160361 9.37757 0.0980532 9.37757 0.577825V2.70757L9.15299 2.72268C5.59039 3.00547 3.50344 5.25989 3.00472 9.3501C2.94067 9.87538 3.54318 10.2042 3.93261 9.85643C5.36448 8.57792 6.84382 7.78398 8.37757 7.46573C8.6236 7.41468 8.87103 7.37587 9.11989 7.34926L9.37757 7.32611V9.42161L9.38294 9.50392C9.4429 9.95663 9.99197 10.1699 10.3298 9.83772ZM9.2263 3.72L10.3776 3.64256V1.61104L13.8238 4.99905L10.3776 8.38816V6.23223L9.02198 6.35403L9.01358 6.35493C7.31135 6.53692 5.70337 7.21889 4.18674 8.35551C4.48493 7.01744 4.99417 6.01008 5.6632 5.28852C6.49235 4.39426 7.65261 3.84605 9.2263 3.72ZM2.5 1C1.11929 1 0 2.11929 0 3.5V11.5C0 12.8807 1.11929 14 2.5 14H10.5C11.8807 14 13 12.8807 13 11.5V10.5C13 10.2239 12.7761 10 12.5 10C12.2239 10 12 10.2239 12 10.5V11.5C12 12.3284 11.3284 13 10.5 13H2.5C1.67157 13 1 12.3284 1 11.5V3.5C1 2.67157 1.67157 2 2.5 2H5.5C5.77614 2 6 1.77614 6 1.5C6 1.22386 5.77614 1 5.5 1H2.5Z"
                fill="#065B70"
              />
            </svg>
          </ActionButton>
          <ActionButton>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
            >
              <path
                d="M16 9C13.7939 9 12 10.7939 12 13C12 13.2761 12.2239 13.5 12.5 13.5C12.7761 13.5 13 13.2761 13 13C13 11.3461 14.3461 10 16 10C17.6539 10 19 11.3461 19 13C19 14.2486 18.3078 14.8633 17.4246 15.6204L17.3935 15.647C16.5328 16.3845 15.5 17.2695 15.5 19V19.5C15.5 19.7761 15.7239 20 16 20C16.2761 20 16.5 19.7761 16.5 19.5V19C16.5 17.7514 17.1922 17.1367 18.0754 16.3796L18.1065 16.353C18.9672 15.6155 20 14.7305 20 13C20 10.7939 18.2061 9 16 9ZM16 23C16.4142 23 16.75 22.6642 16.75 22.25C16.75 21.8358 16.4142 21.5 16 21.5C15.5858 21.5 15.25 21.8358 15.25 22.25C15.25 22.6642 15.5858 23 16 23Z"
                fill="#065B70"
              />
            </svg>
          </ActionButton>
        </ActionButtons> */}
    </ContentContainer>
  );
}
